import React from 'react';
import { OrderStatus } from '../../types';

// ── Status Badge ──────────────────────────────────────────────────────────────
const statusMap: Record<OrderStatus, { bg: string; color: string; border: string }> = {
  'Draft':                  { bg:'#f3f0ea', color:'#6b5e4e', border:'#e0d8cc' },
  'Submitted':              { bg:'#e8f0fc', color:'#1e4a8a', border:'#c5d8f8' },
  'Payment Received':       { bg:'#e8f5ee', color:'#1a7a4a', border:'#b8e8ce' },
  'Admin Review':           { bg:'#fef6e4', color:'#92610a', border:'#fde8a8' },
  'Approved':               { bg:'#e8f5ee', color:'#1a7a4a', border:'#b8e8ce' },
  'Field Staff Assigned':   { bg:'#f3e8fc', color:'#6a1ab8', border:'#ddb8f8' },
  'Installation Completed': { bg:'#e4f6fc', color:'#0a6880', border:'#a8e0f0' },
  'Proof Uploaded':         { bg:'#fff3ec', color:'#c2490e', border:'#fdd0b0' },
  'Campaign Closed':        { bg:'#f3f0ea', color:'#6b5e4e', border:'#e0d8cc' },
  'Rejected':               { bg:'#fde8e8', color:'#b91c1c', border:'#f8c0c0' },
};

export const StatusBadge: React.FC<{ status: OrderStatus; size?: 'sm'|'md' }> = ({ status, size='md' }) => {
  const s = statusMap[status] || statusMap['Draft'];
  return (
    <span style={{ background:s.bg, color:s.color, border:`1px solid ${s.border}`, padding: size==='sm'?'2px 9px':'4px 11px', borderRadius:20, fontSize:size==='sm'?11:12, fontFamily:'DM Sans,sans-serif', fontWeight:600, display:'inline-flex', alignItems:'center', gap:5, whiteSpace:'nowrap' }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:s.color, display:'inline-block', opacity:0.8 }} />
      {status}
    </span>
  );
};

// ── Stat Card ─────────────────────────────────────────────────────────────────
interface StatCardProps { title:string; value:string|number; icon:React.ReactNode; change?:string; positive?:boolean; color?:string; }
export const StatCard: React.FC<StatCardProps> = ({ title, value, icon, change, positive, color='#e8621a' }) => (
  <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'22px 24px', boxShadow:'var(--shadow-sm)', position:'relative', overflow:'hidden' }}>
    <div style={{ position:'absolute', top:0, right:0, width:90, height:90, borderRadius:'0 var(--radius-lg) 0 90px', background:`${color}08` }} />
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
      <div>
        <div style={{ color:'var(--text-muted)', fontSize:12, fontWeight:500, marginBottom:8, textTransform:'uppercase', letterSpacing:'0.06em' }}>{title}</div>
        <div style={{ color:'var(--text-primary)', fontWeight:700, fontSize:28, lineHeight:1 }}>{value}</div>
        {change && <div style={{ color:positive?'var(--success)':'var(--danger)', fontSize:12, marginTop:7, fontWeight:500 }}>{positive?'↑':'↓'} {change}</div>}
      </div>
      <div style={{ width:46, height:46, borderRadius:'var(--radius-md)', background:`${color}12`, display:'flex', alignItems:'center', justifyContent:'center', color, flexShrink:0 }}>
        {icon}
      </div>
    </div>
  </div>
);

// ── Page Header ───────────────────────────────────────────────────────────────
export const PageHeader: React.FC<{ title:string; subtitle?:string; action?:React.ReactNode }> = ({ title, subtitle, action }) => (
  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28, flexWrap:'wrap', gap:12 }}>
    <div>
      <h1 style={{ color:'var(--text-primary)', fontWeight:700, fontSize:24, margin:0, letterSpacing:'-0.02em' }}>{title}</h1>
      {subtitle && <p style={{ color:'var(--text-muted)', fontSize:13, margin:'5px 0 0' }}>{subtitle}</p>}
    </div>
    {action}
  </div>
);

// ── Card ──────────────────────────────────────────────────────────────────────
export const Card: React.FC<{ children:React.ReactNode; style?:React.CSSProperties; className?:string; onClick?:()=>void }> = ({ children, style, className, onClick }) => (
  <div className={className} onClick={onClick} style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:24, boxShadow:'var(--shadow-sm)', ...style }}>
    {children}
  </div>
);

// ── Section Title ─────────────────────────────────────────────────────────────
export const SectionTitle: React.FC<{ children: React.ReactNode; action?:React.ReactNode }> = ({ children, action }) => (
  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
    <h3 style={{ color:'var(--text-primary)', fontWeight:700, fontSize:16, margin:0, letterSpacing:'-0.01em' }}>{children}</h3>
    {action}
  </div>
);

// ── Empty State ───────────────────────────────────────────────────────────────
export const EmptyState: React.FC<{ icon:React.ReactNode; title:string; subtitle?:string; action?:React.ReactNode }> = ({ icon, title, subtitle, action }) => (
  <div style={{ textAlign:'center', padding:'56px 24px' }}>
    <div style={{ width:64, height:64, borderRadius:'50%', background:'var(--bg-muted)', display:'inline-flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)', marginBottom:16 }}>{icon}</div>
    <h3 style={{ color:'var(--text-primary)', fontWeight:600, fontSize:17, margin:'0 0 6px' }}>{title}</h3>
    {subtitle && <p style={{ color:'var(--text-muted)', fontSize:13, margin:'0 0 20px' }}>{subtitle}</p>}
    {action}
  </div>
);

// ── Pill Tab ──────────────────────────────────────────────────────────────────
export const PillTabs: React.FC<{ tabs:{key:string;label:string}[]; active:string; onChange:(k:string)=>void }> = ({ tabs, active, onChange }) => (
  <div style={{ display:'flex', gap:6, background:'var(--bg-muted)', borderRadius:12, padding:4, flexWrap:'wrap' }}>
    {tabs.map(t => (
      <button key={t.key} onClick={() => onChange(t.key)}
        style={{ background: active===t.key?'#fff':'transparent', color: active===t.key?'var(--text-primary)':'var(--text-muted)', border:'none', borderRadius:9, padding:'6px 16px', fontFamily:'DM Sans,sans-serif', fontWeight:500, fontSize:13, cursor:'pointer', boxShadow: active===t.key?'var(--shadow-sm)':'none', transition:'all 0.2s' }}>
        {t.label}
      </button>
    ))}
  </div>
);
